import axios from 'axios';

// Create axios instance with base configuration
const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'http://localhost:5001/api',
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor for logging
api.interceptors.request.use(
  (config) => {
    console.log(`Making ${config.method.toUpperCase()} request to ${config.url}`);
    return config;
  },
  (error) => {
    console.error('Request error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    console.error('API Error:', error.response?.data || error.message);
    return Promise.reject(error);
  }
);

// URL validation and site mapping
export const validateUrl = async (url) => {
  try {
    const response = await api.post('/validate-url', { url });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.error || 'Failed to validate URL');
  }
};

export const mapSite = async (url, maxDepth = 3, maxPages = 1000, urlFilters = null) => {
  try {
    const response = await api.post('/map-site', { 
      url, 
      max_depth: maxDepth,
      max_pages: maxPages,
      url_filters: urlFilters
    });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.error || 'Failed to map site');
  }
};

export const getMapStatus = async (jobId) => {
  try {
    const response = await api.get(`/map-status/${jobId}`);
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.error || 'Failed to get mapping status');
  }
};

// Scraping operations
export const startScraping = async (urls, driveFolderId = null) => {
  try {
    const response = await api.post('/scrape/start', {
      urls,
      drive_folder_id: driveFolderId
    });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.error || 'Failed to start scraping');
  }
};

export const getScrapingStatus = async (jobId) => {
  try {
    const response = await api.get(`/scrape/status/${jobId}`);
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.error || 'Failed to get scraping status');
  }
};

export const cancelScraping = async (jobId) => {
  try {
    const response = await api.post(`/scrape/cancel/${jobId}`);
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.error || 'Failed to cancel scraping');
  }
};

// Google Drive operations
export const getDriveAuthUrl = async () => {
  try {
    const response = await api.get('/drive/auth');
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.error || 'Failed to get auth URL');
  }
};


export const getDriveStatus = async () => {
  try {
    const response = await api.get('/drive/status');
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.error || 'Failed to get Drive status');
  }
};

export const getDriveAccessToken = async () => {
  try {
    const response = await api.get('/drive/access-token');
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.error || 'Failed to get access token');
  }
};

export const listDriveFolders = async (parentId = 'root') => {
  try {
    const response = await api.get('/drive/folders', {
      params: { parent_id: parentId }
    });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.error || 'Failed to list folders');
  }
};

export const createDriveFolder = async (name, parentId = 'root') => {
  try {
    const response = await api.post('/drive/folders', {
      name,
      parent_id: parentId
    });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.error || 'Failed to create folder');
  }
};

export const createScrapingFolder = async (url) => {
  try {
    const response = await api.post('/drive/scraping-folder', { url });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.error || 'Failed to create scraping folder');
  }
};

export const disconnectDrive = async () => {
  try {
    const response = await api.post('/drive/disconnect');
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.error || 'Failed to disconnect Drive');
  }
};

export const saveScrapedContentToDrive = async (jobId, folderId, onProgress = null) => {
  try {
    // Use a longer timeout for Google Drive uploads (5 minutes)
    const response = await api.post('/drive/save-scraped-content', {
      job_id: jobId,
      folder_id: folderId
    }, {
      timeout: 300000 // 5 minutes for large uploads
    });
    return response.data;
  } catch (error) {
    // Check if it's a timeout error specifically
    if (error.code === 'ECONNABORTED' && error.message.includes('timeout')) {
      throw new Error('Upload timeout - files may still be uploading to Google Drive. Please check your Google Drive folder.');
    }
    throw new Error(error.response?.data?.error || 'Failed to save content to Google Drive');
  }
};

export const getDriveUploadProgress = async (jobId) => {
  try {
    const response = await api.get(`/drive/upload-progress/${jobId}`);
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.error || 'Failed to get upload progress');
  }
};

export const getDriveFileInfo = async (fileId) => {
  try {
    const response = await api.get(`/drive/files/${fileId}`);
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.error || 'Failed to get file info');
  }
};

export const deleteDriveFile = async (fileId) => {
  try {
    const response = await api.delete(`/drive/files/${fileId}`);
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.error || 'Failed to delete file');
  }
};

// Health check
export const healthCheck = async () => {
  try {
    const response = await api.get('/health');
    return response.data;
  } catch (error) {
    throw new Error('Health check failed');
  }
};

export default api;
